import { Button, DisplayText } from '@superops/ui-design-system';

import {
  BackgroundIcon,
  ERROR_CODES,
  getTranslatedText
} from '@nile/components';
import React, { useState } from 'react';
import dompurify from 'dompurify';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ERROR_CODES_ITEM_MAP } from '@nile/client_management/src/Constants/invoice';
import { Tooltip } from 'react-clone-tippy';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { dompurifier } from '@nile/components/src/Utils/helpers';

/**
 * Cells are specific to the components using the table.
 * This action cell is used to push the invoice to third party.
 */

const integrationMap = {
  send: 'invoice.actions.sendInvoice',
  resend: 'invoice.actions.resendInvoice'
};

const concatErrorsArray = (errorsArray, intl) => {
  return errorsArray.reduce((acc, error) => {
    let items = '';

    if (error.params) {
      const itemsArray =
        error.params[ERROR_CODES_ITEM_MAP[error.errorCode]] ||
        error.params.items ||
        [];
      const itemStrings = itemsArray.map((item) => `${item.name}`);
      items = `(${itemStrings.join(', ')})`;
    }

    return `${acc}${getTranslatedText(
      intl,
      ERROR_CODES[error.errorCode] || error.message || ' '
    )} ${items}\n`;
  }, '');
};

const getSendInvoiceErrorMessage = (col, intl) => {
  const { sendEmailError } = col;
  let errors = [];
  if (sendEmailError) errors = [...sendEmailError];

  return concatErrorsArray(errors, intl);
};

function ActionCell(props) {
  const { cell, intl } = props;
  const meta = cell?.row?.original;
  const [actionLoading, setActionLoading] = useState(false);
  const { actions, integrationType = 'DEFAULT' } = cell.column;

  const status = meta?.emailOperationalStatus;

  const sendToClientLoading = status === 'PROGRESS';

  const { loadingState } = cell;
  const sendActionKey = meta.sentToClient ? 'resend' : 'send';

  const getIcon = () => {
    switch (status) {
      case 'SUCCESS':
        return <SVGIcon name="OutgoingMail" color="green" size="sm" />;
      case 'PROGRESS':
        return <SVGIcon name="HourGlassWirePaused" size="sm" />;
      case 'FAILURE':
        const errorMessage = getSendInvoiceErrorMessage(meta, intl);
        return (
          errorMessage && (
            <div className="mlauto">
              <Tooltip
                distance={10}
                html={<span className="ws-prewrap">{errorMessage}</span>}
                position="top-start"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
                className="tooltip"
              >
                <SVGIcon name="InlineError" size="sm" color="red" />
              </Tooltip>
            </div>
          )
        );
      default:
        return '';
    }
  };

  const renderStatus = () => {
    return (
      <div className="flex actr g10">
        <div className="fx actr g10">
          {getIcon()}
          <FormattedMessage
            id={`invoice.email.status.${status?.toLowerCase()}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="position-relative">
      {!cell?.column?.hideError && (
        <div className="action-button-bg position-absolute full-width full-height flex center">
          {status !== null ? (
            renderStatus()
          ) : (
            <div className="full-width ellipsis text-black hidden">
              <DisplayText element="p" size="large">
                <FormattedMessage id="client.details.invoice.listView.action.empty.backGroundText" />
              </DisplayText>
            </div>
          )}
        </div>
      )}
      <div className="action-button flex center g20">
        <ButtonContainer loadingState={loadingState}>
          {status === 'FAILURE' && renderStatus()}
          <Button
            disabled={actionLoading || loadingState || sendToClientLoading}
            onClick={() => {
              actions.onClick(meta, integrationType, setActionLoading);
              setActionLoading(true);
            }}
          >
            {actionLoading || loadingState || sendToClientLoading ? (
              <div className="flex actr jctr capitalize">
                <FormattedMessage id="app.common.email.status.sending" />
              </div>
            ) : (
              <div className="flex actr">
                <SVGIcon name="InvoiceLogo" />
                <span className="ml-5">
                  <FormattedMessage id={integrationMap[sendActionKey]} />
                </span>
              </div>
            )}
          </Button>
        </ButtonContainer>
      </div>
    </div>
  );
}

const ButtonContainer = (props) => {
  const { intl, loadingState, children } = props;
  if (loadingState) {
    return (
      <Tooltip
        html={
          <p
            dangerouslySetInnerHTML={{
              __html: dompurifier(
                getTranslatedText(
                  intl,
                  'client.details.invoice.sendToClient.loading'
                )
              )
            }}
          />
        }
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
        className="tooltip"
      >
        {children}
      </Tooltip>
    );
  }
  return <>{children}</>;
};

export default injectIntl(ActionCell);
